import { isShowCardAllowed } from '@/screens/bdmc-summary/CardDetails/utils/cardHelpers'
import { withWorldReadyCountrySwitching } from '@/screens/bdmc/shared/l10n'
import { formatPartialCardNumber } from 'utils/formatter'

export const CHURNFLOW_STRINGS_PREFIX = 'components.v2.strings'
export const CHURNFLOW_CARD_SUFFIX_TOKEN = '@cardSuffix'

type ChurnflowCard =
  | {
      cardNumberSuffix?: string
      card_number_suffix?: string
      capabilities?: string[]
    }
  | null
  | undefined

export function getChurnflowCardSuffix(card: ChurnflowCard, cardSuffix?: string): string {
  if (cardSuffix) {
    return cardSuffix
  }
  const suffix = card?.cardNumberSuffix ?? card?.card_number_suffix
  return formatPartialCardNumber(suffix)
}

/** Legacy churnflow strings use `@cardSuffix`; WorldReady expects `{cardSuffix}`. */
export function applyChurnflowCardSuffix(message: string, cardSuffix: string): string {
  return message.replace(CHURNFLOW_CARD_SUFFIX_TOKEN, cardSuffix)
}

export function formatChurnflowMessageWithCardSuffix(
  formatMessageFn: (msgId: string, msgVars?: Record<string, unknown>) => string,
  messageId: string,
  cardSuffix: string,
  messageVars?: Record<string, unknown>,
): string {
  return applyChurnflowCardSuffix(formatMessageFn(messageId, messageVars), cardSuffix)
}

/** Port of legacy churnFlowHelper.formatMessageId — appends `-v2` only for keys that have a v2 locale entry. */
const CHURNFLOW_V2_SUFFIX_KEYS = new Set([
  'churnflow.cancel-options.messages.one',
  'churnflow.replace.general.send.replacement.card',
  'churnflow.replace.general.update-address',
  'churnflow.replace.stolen.title',
  'churnflow.replace.success.header',
])

const CHURNFLOW_V2_SUFFIX_PREFIXES = [
  'churnflow.main-options.options.',
  'churnflow.replace.damaged.',
  'churnflow.replace.missing.',
  'churnflow.replace.stolen.messages.',
  'churnflow.replace.stolen.success.',
  'churnflow.replace.not-right.',
  'churnflow.replace.try-again.',
  'churnflow.replace.nameChange.success.',
  'churnflow.name-change.',
  'churnflow.canceled.try-again.',
  'churnflow.canceled.call-us.',
]

export function shouldUseChurnflowV2Suffix(key: string): boolean {
  if (!key || key.endsWith('-v2')) {
    return false
  }

  if (CHURNFLOW_V2_SUFFIX_KEYS.has(key)) {
    return true
  }

  return CHURNFLOW_V2_SUFFIX_PREFIXES.some((prefix) => key === prefix || key.startsWith(prefix))
}

export function formatChurnflowMessageKey(
  card: { capabilities?: string[] } | null | undefined,
  key: string,
): string {
  if (!isShowCardAllowed(card) || !shouldUseChurnflowV2Suffix(key)) {
    return key
  }
  return `${key}-v2`
}

export function churnflowMessageId(
  card: { capabilities?: string[] } | null | undefined,
  key: string,
): string {
  return `${CHURNFLOW_STRINGS_PREFIX}.${formatChurnflowMessageKey(card, key)}`
}

/** Legacy replace flow resolves churnflow keys with -v2 suffix and country overlays (e.g. GB → _EU). */
export function createReplaceCardMessageResolver(
  card: ChurnflowCard,
  country: string,
  worldReady: unknown,
) {
  const churnflowI18n = withWorldReadyCountrySwitching(
    { country },
    worldReady,
    `${CHURNFLOW_STRINGS_PREFIX}.`,
  )

  return (messageId: string) => {
    if (!messageId?.startsWith('churnflow.')) {
      return messageId
    }

    return churnflowI18n.keyName(formatChurnflowMessageKey(card, messageId))
  }
}

/** Resolves replace-card content ids: churnflow keys get the v2 strings namespace and suffix. */
export function resolveReplaceCardMessageId(
  card: { capabilities?: string[] } | null | undefined,
  messageId: string,
  country?: string,
  worldReady?: unknown,
): string {
  if (!messageId?.startsWith('churnflow.')) {
    return messageId
  }

  if (country && worldReady) {
    return createReplaceCardMessageResolver(card, country, worldReady)(messageId)
  }

  return churnflowMessageId(card, messageId)
}
